import React from "react";
import "./UploadedFileList.css";
import { getApiBaseUrl as sharedApiBaseUrl } from "../services/api";

/** Compact size like (854K) or (2,556K) */
export const formatFileSizeCompact = (bytes) => {
  if (bytes == null || bytes === "") return "";
  const n = Number(bytes);
  if (!n || Number.isNaN(n)) return "";
  const k = Math.round(n / 1024);
  if (k < 1) return "(<1K)";
  return `(${k.toLocaleString("en-US")}K)`;
};

export const resolveUploadedFileUrl = (pathOrUrl) => {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl).trim();
  if (!s) return "";
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  const base = sharedApiBaseUrl() || "";
  return base ? `${base.replace(/\/$/, "")}/${s.replace(/^\//, "")}` : `/${s.replace(/^\//, "")}`;
};

export const getUploadedFileSize = (item) =>
  item?.file?.size ?? item?.size ?? item?.fileSize ?? 0;

export const getUploadedFileName = (item) => {
  const name = item?.name;
  if (name) return name;
  const path = item?.filePath || item?.path || item?.url || "";
  if (!path) return "File";
  try {
    return decodeURIComponent(String(path).split("/").pop() || "File");
  } catch {
    return String(path).split("/").pop() || "File";
  }
};

export const isUploadedPdf = (item) => {
  const t = (item?.type || item?.fileType || item?.file?.type || "").toLowerCase();
  const name = (item?.name || "").toLowerCase();
  return t.includes("pdf") || /\.pdf$/i.test(name);
};

export const isUploadedImage = (item) => {
  const t = (item?.type || item?.fileType || item?.file?.type || "").toLowerCase();
  const name = (item?.name || "").toLowerCase();
  return (
    t.startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|bmp)$/i.test(name) ||
    !!item?.preview
  );
};

const getBadgeMeta = (item) => {
  const isPdf = isUploadedPdf(item);
  const isImage = isUploadedImage(item);
  if (isPdf) {
    return {
      badgeText: "PDF",
      badgeClass: "uploaded-file-list__type-badge uploaded-file-list__type-badge--pdf",
      iconClass: "bi bi-filetype-pdf",
    };
  }
  if (isImage) {
    return {
      badgeText: "IMG",
      badgeClass: "uploaded-file-list__type-badge uploaded-file-list__type-badge--img",
      iconClass: "bi bi-image",
    };
  }
  return {
    badgeText: "FILE",
    badgeClass: "uploaded-file-list__type-badge uploaded-file-list__type-badge--file",
    iconClass: "bi bi-file-earmark",
  };
};

/**
 * Horizontal list of uploaded files: badge + blue filename + size + optional remove.
 */
const UploadedFileList = ({
  files = [],
  onRemove,
  onOpen,
  className = "",
  style,
  noTopMargin = false,
  getItemKey = (item, index) => item?.id ?? index,
}) => {
  if (!files || files.length === 0) return null;

  const handleOpen = (item) => {
    if (onOpen) {
      onOpen(item);
      return;
    }
    const url =
      item?.url ||
      (item?.file ? URL.createObjectURL(item.file) : "") ||
      resolveUploadedFileUrl(item?.path || item?.filePath);
    if (url) window.open(url, "_blank");
  };

  const listClass = [
    "uploaded-file-list",
    noTopMargin ? "uploaded-file-list--no-top-margin" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={listClass} style={style} role="list">
      {files.map((item, index) => {
        const key = getItemKey(item, index);
        const name = getUploadedFileName(item);
        const sizeLabel = formatFileSizeCompact(getUploadedFileSize(item));
        const { badgeText, badgeClass, iconClass } = getBadgeMeta(item);
        const canOpen = Boolean(
          onOpen ||
            item?.url ||
            item?.file ||
            item?.preview ||
            item?.path ||
            item?.filePath
        );

        return (
          <div key={key} className="uploaded-file-list__row">
            <div className="uploaded-file-list__badges">
              <span className="uploaded-file-list__icon-wrap" title={badgeText}>
                <i className={iconClass} />
              </span>
              <span className={badgeClass}>{badgeText}</span>
            </div>
            <button
              type="button"
              className="uploaded-file-list__name-btn"
              title={name}
              disabled={!canOpen}
              onClick={() => {
                if (canOpen) handleOpen(item);
              }}
            >
              {name}
            </button>
            {sizeLabel ? (
              <span className="uploaded-file-list__size">{sizeLabel}</span>
            ) : null}
            {onRemove ? (
              <button
                type="button"
                className="uploaded-file-list__remove"
                title="Remove file"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(item);
                }}
              >
                ×
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export default UploadedFileList;
